import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Music, Link as LinkIcon, ListMusic, Info, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

const Landing = () => {
  const navigate = useNavigate();
  const [slugInput, setSlugInput] = useState('');
  const steps = [
    {
      icon: LinkIcon,
      title: 'Open your league URL',
      description: 'Add your league slug to the URL and press enter.',
    },
    {
      icon: ListMusic,
      title: 'Pick a playlist',
      description: 'Choose curated playlists or filter by season, submitter, and rank.',
    },
    {
      icon: Music,
      title: 'Play and explore',
      description: 'Listen to submissions and browse comments, fun facts, and results.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Simple Header */}
      <nav className="bg-card/80 backdrop-blur-sm border-b border-border fixed w-full z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-lg bg-music-gradient flex items-center justify-center">
                <Music className="h-5 w-5 text-white" />
              </div>
              <span className="text-xl font-bold text-foreground">MusicLeaguePlayer</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-12 lg:pb-16 bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-4 py-2 text-sm text-primary mb-6">
            <Info className="h-4 w-4" />
            No login required — just use your league URL
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-4">
            How to open your league
          </h1>
          <p className="text-lg text-muted-foreground mb-6">
            Enter your league slug to open playlists.
          </p>
          <div className="max-w-2xl mx-auto mb-10">
            <form
              className="flex flex-col gap-3 sm:flex-row sm:items-center"
              onSubmit={(event) => {
                event.preventDefault();
                const trimmed = slugInput.trim();
                if (!trimmed) return;
                navigate(`/${trimmed}`);
              }}
            >
              <Input
                value={slugInput}
                onChange={(event) => setSlugInput(event.target.value)}
                placeholder="Enter your league slug"
                className="h-11"
              />
              <Button type="submit" className="btn-music h-11">
                Go to League
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </form>
            <p className="mt-2 text-xs text-muted-foreground">
              Slug matching is not case-sensitive.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">
              What happens next
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Three simple steps to start listening
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <Card key={index} className="card-hover text-center p-8">
                <CardContent className="pt-6">
                  <div className="w-16 h-16 rounded-full bg-music-gradient flex items-center justify-center mx-auto mb-6">
                    <step.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold text-foreground mb-4">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-muted-foreground">
          <p>Use your league slug in the URL to access playlists and results.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;